import React, { useCallback, useMemo, useRef, useState } from "react";
import { useCreateFaithLeadMutation } from "../../app/api/faithApi";
import heroImg from "../../assets/images/Blurred suit.png";

type FormState = {
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
};

const SectionIcon: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="w-14 h-14 rounded-xl flex items-center justify-center bg-white shadow-sm border border-gray-200">
    {children}
  </div>
);

const FaithThatWorks: React.FC = () => {
  const [createFaithLead, { isLoading }] = useCreateFaithLeadMutation();

  const mountedRef = useRef(true);
  React.useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const [form, setForm] = useState<FormState>({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
  });
  const [statusMessage, setStatusMessage] = useState<string | null>(null);

  const defaultSource = useMemo(() => "faith_that_works_prereg", []);

  const isValidEmail = useCallback((email: string) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  }, []);

  const updateField = useCallback((k: keyof FormState, v: string) => {
    setForm((p) => ({ ...p, [k]: v }));
  }, []);

  const handleSubmit = useCallback(
    async (e?: React.FormEvent) => {
      e?.preventDefault();
      setStatusMessage(null);

      if (!form.email || !isValidEmail(form.email)) {
        setStatusMessage("Please enter a valid email address.");
        return;
      }

      try {
        const payload = {
          firstName: form.firstName,
          lastName: form.lastName,
          email: form.email,
          phoneNo: form.phone,
          source: defaultSource,
        };

        await createFaithLead(payload).unwrap();

        if (!mountedRef.current) return;
        setStatusMessage(
          "Thanks — you're pre-registered! Check your email for next steps."
        );
        setForm({ firstName: "", lastName: "", email: "", phone: "" });
      } catch (err) {
        console.error("Pre-reg submit error:", err);
        if (!mountedRef.current) return;
        setStatusMessage("Something went wrong. Please try again.");
      }
    },
    [form, isValidEmail, createFaithLead, defaultSource]
  );

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white text-gray-900">
      {/* HERO SECTION - PASTOR: Problem */}
      <section className="relative bg-brandNavy text-white overflow-hidden">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmYiIGZpbGwtb3BhY2l0eT0iMC4wNSI+PHBhdGggZD0iTTM2IDM0djItMnptMC0ydjItMnptLTItMmgyLTJ6bTAtMmgyLTJ6bS0yLTJoMi0yem0wLTJoMi0yem0tMi0yaDItMnptMC0yaDItMnptLTItMmgyLTJ6bTAtMmgyLTJ6bS0yLTJoMi0yem0wLTJoMi0yem0tMi0yaDItMnptMC0yaDItMnoiLz48L2c+PC9nPjwvc3ZnPg==')] opacity-30"></div>

        <div className="relative max-w-7xl mx-auto px-6 py-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 ">
            {/* Left: Headline + Problem */}
            <div className="space-y-6">
              <h1 className="text-5xl font-extrabold leading-tight">
                Turn Your God-Given Vision Into{" "}
                <span className="text-brandRed"> Measurable Reality</span>
              </h1>

              <p className="text-xl md:text-2xl text-blue-100 leading-relaxed">
                Stop hoping your vision will somehow work out. Learn the proven
                framework that helps pastors, founders, and kingdom leaders
                build sustainable outcomes—without losing the faith that started
                it all.
              </p>

              <div className="flex flex-col sm:flex-row gap-4 pt-4">
                <button
                  onClick={() => {
                    const el = document.getElementById("pre-register-form");
                    el?.scrollIntoView({ behavior: "smooth", block: "center" });
                  }}
                  className="px-8 py-4 bg-red-600 text-white text-lg font-bold rounded-lg shadow-xl hover:bg-red-700 transition-all transform hover:scale-105"
                >
                  Pre-Register Now (Free)
                </button>
              </div>
            </div>

            {/* Right: Hero Image */}
            <div className="relative">
              <div className="relative rounded-2xl overflow-hidden shadow-2xl border-4 border-white/20">
                <img
                  src={heroImg}
                  alt="Leader with vision"
                  className="w-full h-auto object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-blue-900/60 to-transparent"></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* PASTOR: Amplify - Pain Points with Images */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Does This Sound Familiar?
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              You're not alone. These are the challenges every visionary leader
              faces—but they don't have to hold you back.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Pain Point 1 */}
            <div className="group relative bg-gradient-to-br from-gray-50 to-white rounded-2xl p-8 shadow-lg border border-gray-200 hover:shadow-2xl transition-all">
              <div className="mb-6 overflow-hidden rounded-xl">
                <img
                  src="https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?w=600&q=80"
                  alt="Clarity confusion"
                  className="w-full h-48 object-cover group-hover:scale-110 transition-transform duration-300"
                />
              </div>
              <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mb-4">
                <span className="text-2xl">❌</span>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-3">
                Vision Without Direction
              </h3>
              <p className="text-gray-600 leading-relaxed">
                You have a God-given vision, but no clear roadmap. You're stuck
                in "spiritual limbo"—praying, believing, but not seeing tangible
                progress. The gap between promise and reality feels
                overwhelming.
              </p>
            </div>

            {/* Pain Point 2 */}
            <div className="group relative bg-gradient-to-br from-gray-50 to-white rounded-2xl p-8 shadow-lg border border-gray-200 hover:shadow-2xl transition-all">
              <div className="mb-6 overflow-hidden rounded-xl">
                <img
                  src="https://images.unsplash.com/photo-1552664730-d307ca884978?w=600&q=80"
                  alt="Scattered efforts"
                  className="w-full h-48 object-cover group-hover:scale-110 transition-transform duration-300"
                />
              </div>
              <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mb-4">
                <span className="text-2xl">⚡</span>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-3">
                Scattered Execution
              </h3>
              <p className="text-gray-600 leading-relaxed">
                You're working hard but spinning your wheels. Multiple
                initiatives, no systems. You lack the structure to turn
                revelation into repeatable action, leaving you exhausted with
                little to show for it.
              </p>
            </div>

            {/* Pain Point 3 */}
            <div className="group relative bg-gradient-to-br from-gray-50 to-white rounded-2xl p-8 shadow-lg border border-gray-200 hover:shadow-2xl transition-all">
              <div className="mb-6 overflow-hidden rounded-xl">
                <img
                  src="https://images.unsplash.com/photo-1551836022-4c4c79ecde51?w=600&q=80"
                  alt="Lost momentum"
                  className="w-full h-48 object-cover group-hover:scale-110 transition-transform duration-300"
                />
              </div>
              <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mb-4">
                <span className="text-2xl">📉</span>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-3">
                Lost Momentum
              </h3>
              <p className="text-gray-600 leading-relaxed">
                You start strong but can't sustain progress. Without
                accountability and measurement, motivation fades. You're
                constantly restarting instead of building momentum toward
                lasting kingdom impact.
              </p>
            </div>
          </div>

          <div className="mt-12 text-center">
            <div className="inline-block bg-red-50 border-2 border-red-200 rounded-xl p-6 max-w-2xl">
              <p className="text-lg text-gray-700">
                <span className="font-bold text-red-600">
                  Here's the truth:
                </span>{" "}
                Faith without works is dead—but works without a framework is
                exhausting. You need both spiritual sensitivity AND practical
                systems.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* PASTOR: Story + Solution - The APE Framework */}
      <section
        id="how-it-works"
        className="py-20 bg-gradient-to-br from-blue-50 to-gray-50"
      >
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <div className="inline-block px-4 py-2 bg-blue-100 text-blue-800 rounded-full text-sm font-semibold mb-4">
              The Solution
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              The APE Framework™
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              A proven, faith-based system that transforms visionary leaders
              into execution powerhouses. This isn't theory—it's a
              battlefield-tested framework used by pastors, founders, and
              kingdom builders to produce predictable results.
            </p>
          </div>

          {/* Framework Steps */}
          <div className="grid gap-12 max-w-5xl mx-auto">
            {/* Anticipate */}
            <div className="relative bg-white rounded-2xl shadow-xl overflow-hidden border border-gray-200">
              <div className="grid grid-cols-1 md:grid-cols-2">
                <div className="order-2 md:order-1 p-8 md:p-12 flex flex-col justify-center">
                  <div className="flex items-center gap-4 mb-6">
                    <div className="w-16 h-16 bg-blue-600 text-white rounded-xl flex items-center justify-center text-2xl font-bold shadow-lg">
                      1
                    </div>
                    <div>
                      <div className="text-sm text-blue-600 font-semibold uppercase tracking-wide">
                        Step One
                      </div>
                      <h3 className="text-3xl font-bold text-gray-900">
                        Anticipate
                      </h3>
                    </div>
                  </div>

                  <p className="text-lg text-gray-700 mb-6 leading-relaxed">
                    <span className="font-bold">
                      Discern spiritual seasons and identify strategic
                      opportunities.
                    </span>{" "}
                    Most leaders miss divine timing because they're reacting
                    instead of anticipating. We teach you to read the signs,
                    spot kairos moments, and position yourself before the
                    opportunity arrives.
                  </p>

                  <ul className="space-y-3 text-gray-600">
                    <li className="flex items-start gap-3">
                      <svg
                        className="w-6 h-6 text-green-500 flex-shrink-0 mt-0.5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                      <span>
                        Seasonal discernment tools to align your work with God's
                        timing
                      </span>
                    </li>
                    <li className="flex items-start gap-3">
                      <svg
                        className="w-6 h-6 text-green-500 flex-shrink-0 mt-0.5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                      <span>
                        Opportunity mapping frameworks to spot what others miss
                      </span>
                    </li>
                    <li className="flex items-start gap-3">
                      <svg
                        className="w-6 h-6 text-green-500 flex-shrink-0 mt-0.5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                      <span>
                        Strategic foresight practices that reduce wasted effort
                        by 50%
                      </span>
                    </li>
                  </ul>
                </div>

                <div className="order-1 md:order-2">
                  <img
                    src="https://images.unsplash.com/photo-1507842217343-583bb7270b66?w=800&q=80"
                    alt="Anticipate - Vision and foresight"
                    className="w-full h-full object-cover min-h-[300px]"
                  />
                </div>
              </div>
            </div>

            {/* Prepare */}
            <div className="relative bg-white rounded-2xl shadow-xl overflow-hidden border border-gray-200">
              <div className="grid grid-cols-1 md:grid-cols-2">
                <div className="order-2 p-8 md:p-12 flex flex-col justify-center">
                  <div className="flex items-center gap-4 mb-6">
                    <div className="w-16 h-16 bg-yellow-500 text-white rounded-xl flex items-center justify-center text-2xl font-bold shadow-lg">
                      2
                    </div>
                    <div>
                      <div className="text-sm text-yellow-600 font-semibold uppercase tracking-wide">
                        Step Two
                      </div>
                      <h3 className="text-3xl font-bold text-gray-900">
                        Prepare
                      </h3>
                    </div>
                  </div>

                  <p className="text-lg text-gray-700 mb-6 leading-relaxed">
                    <span className="font-bold">
                      Build simple, repeatable systems that bridge vision to
                      action.
                    </span>{" "}
                    This is where revelation meets practical structure. We help
                    you create rhythms, define roles, and establish operating
                    cadences that turn abstract dreams into executable plans.
                  </p>

                  <ul className="space-y-3 text-gray-600">
                    <li className="flex items-start gap-3">
                      <svg
                        className="w-6 h-6 text-green-500 flex-shrink-0 mt-0.5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                      <span>
                        Vision-to-action templates that eliminate confusion and
                        overwhelm
                      </span>
                    </li>
                    <li className="flex items-start gap-3">
                      <svg
                        className="w-6 h-6 text-green-500 flex-shrink-0 mt-0.5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                      <span>
                        Weekly and monthly rhythms that create sustainable
                        momentum
                      </span>
                    </li>
                    <li className="flex items-start gap-3">
                      <svg
                        className="w-6 h-6 text-green-500 flex-shrink-0 mt-0.5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                      <span>
                        Resource allocation tools to maximize impact with
                        limited resources
                      </span>
                    </li>
                  </ul>
                </div>

                <div className="order-1">
                  <img
                    src="https://images.unsplash.com/photo-1542626991-cbc4e32524cc?w=800&q=80"
                    alt="Prepare - Planning and systems"
                    className="w-full h-full object-cover min-h-[300px]"
                  />
                </div>
              </div>
            </div>

            {/* Execute */}
            <div className="relative bg-white rounded-2xl shadow-xl overflow-hidden border border-gray-200">
              <div className="grid grid-cols-1 md:grid-cols-2">
                <div className="order-2 md:order-1 p-8 md:p-12 flex flex-col justify-center">
                  <div className="flex items-center gap-4 mb-6">
                    <div className="w-16 h-16 bg-green-600 text-white rounded-xl flex items-center justify-center text-2xl font-bold shadow-lg">
                      3
                    </div>
                    <div>
                      <div className="text-sm text-green-600 font-semibold uppercase tracking-wide">
                        Step Three
                      </div>
                      <h3 className="text-3xl font-bold text-gray-900">
                        Execute
                      </h3>
                    </div>
                  </div>

                  <p className="text-lg text-gray-700 mb-6 leading-relaxed">
                    <span className="font-bold">
                      Launch focused campaigns, measure progress, and scale what
                      works.
                    </span>{" "}
                    Execution without measurement is just activity. We show you
                    how to run disciplined sprints, track meaningful metrics,
                    and iterate rapidly—turning your prepared plans into
                    tangible outcomes.
                  </p>

                  <ul className="space-y-3 text-gray-600">
                    <li className="flex items-start gap-3">
                      <svg
                        className="w-6 h-6 text-green-500 flex-shrink-0 mt-0.5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                      <span>
                        90-day campaign frameworks for rapid, measurable results
                      </span>
                    </li>
                    <li className="flex items-start gap-3">
                      <svg
                        className="w-6 h-6 text-green-500 flex-shrink-0 mt-0.5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                      <span>
                        KPI tracking systems that reveal what's working (and
                        what's not)
                      </span>
                    </li>
                    <li className="flex items-start gap-3">
                      <svg
                        className="w-6 h-6 text-green-500 flex-shrink-0 mt-0.5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                      <span>
                        Scaling playbooks to amplify impact without burning out
                      </span>
                    </li>
                  </ul>
                </div>

                <div className="order-1 md:order-2">
                  <img
                    src="https://images.unsplash.com/photo-1552664730-d307ca884978?w=800&q=80"
                    alt="Execute - Action and results"
                    className="w-full h-full object-cover min-h-[300px]"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Framework Summary */}
          <div className="mt-16 text-center">
            <div className="inline-block bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-2xl p-8 max-w-3xl shadow-2xl">
              <h3 className="text-2xl font-bold mb-3">The Result?</h3>
              <p className="text-lg leading-relaxed">
                A predictable path from vision to victory. You'll move from
                reactive hoping to proactive building—stewarding your calling
                with clarity, executing with confidence, and producing kingdom
                results that compound over time.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* PASTOR: Testimony - Social Proof */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Leaders Are Getting Results
            </h2>
          </div>

          <div className="mt-12 text-center">
            <button
              onClick={() => {
                const el = document.getElementById("pre-register-form");
                el?.scrollIntoView({ behavior: "smooth", block: "center" });
              }}
              className="mt-6 inline-flex items-center gap-3 px-6 py-3 rounded-lg bg-brandRed text-white font-semibold shadow-lg hover:bg-red-700 transition"
            >
              Join the Next Cohort (Free)
            </button>
          </div>
        </div>
      </section>

      {/* PRE-REGISTER FORM */}
      <section
        id="pre-register-form"
        className="py-20 bg-gradient-to-br from-gray-50 to-white"
      >
        <div className="max-w-3xl mx-auto px-6">
          <div className="bg-white rounded-2xl shadow-xl p-10 border border-gray-200">
            <div className="flex items-start gap-6 mb-6">
              <SectionIcon>
                <svg
                  className="w-6 h-6 text-blue-700"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M16 12a4 4 0 10-8 0v4h8v-4zM12 3v3"
                  />
                </svg>
              </SectionIcon>
              <div>
                <h3 className="text-2xl font-bold text-gray-900">
                  Pre-register for Faith That Works Mentorship
                </h3>
                <p className="text-gray-600">
                  Enter your details to secure a free spot and receive next
                  steps by email.
                </p>
              </div>
            </div>

            <form
              onSubmit={handleSubmit}
              className="grid grid-cols-1 md:grid-cols-2 gap-4"
            >
              <label className="flex flex-col">
                <span className="text-sm text-gray-700 mb-2">First name</span>
                <input
                  value={form.firstName}
                  onChange={(e) => updateField("firstName", e.target.value)}
                  className="px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-200 outline-none"
                  placeholder="First name"
                  type="text"
                  name="firstName"
                  autoComplete="given-name"
                />
              </label>

              <label className="flex flex-col">
                <span className="text-sm text-gray-700 mb-2">Last name</span>
                <input
                  value={form.lastName}
                  onChange={(e) => updateField("lastName", e.target.value)}
                  className="px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-200 outline-none"
                  placeholder="Last name"
                  type="text"
                  name="lastName"
                  autoComplete="family-name"
                />
              </label>

              <label className="flex flex-col md:col-span-2">
                <span className="text-sm text-gray-700 mb-2">Email</span>
                <input
                  value={form.email}
                  onChange={(e) => updateField("email", e.target.value)}
                  className="px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-200 outline-none"
                  placeholder="you@example.com"
                  type="email"
                  name="email"
                  required
                  autoComplete="email"
                />
              </label>

              <label className="flex flex-col md:col-span-2">
                <span className="text-sm text-gray-700 mb-2">
                  Phone (optional)
                </span>
                <input
                  value={form.phone}
                  onChange={(e) => updateField("phone", e.target.value)}
                  className="px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-200 outline-none"
                  placeholder="+234 800 000 0000"
                  type="tel"
                  name="phone"
                  autoComplete="tel"
                />
              </label>

              {/* hidden source */}
              <input type="hidden" name="source" value={defaultSource} />

              <div className="md:col-span-2 flex flex-col gap-3 mt-2">
                <div className="flex items-center justify-between gap-4">
                  <button
                    type="submit"
                    disabled={isLoading}
                    className={`px-6 py-3 rounded-lg text-white font-semibold shadow-md transition ${
                      isLoading
                        ? "bg-gray-400 cursor-not-allowed"
                        : "bg-red-600 hover:bg-red-700"
                    }`}
                  >
                    {isLoading ? "Submitting..." : "Pre-Register (Free)"}
                  </button>

                  <button
                    type="button"
                    onClick={() => {
                      setForm({
                        firstName: "",
                        lastName: "",
                        email: "",
                        phone: "",
                      });
                      setStatusMessage(null);
                    }}
                    className="px-4 py-2 rounded-lg border border-gray-200 bg-white hover:bg-gray-50"
                  >
                    Reset
                  </button>
                </div>

                <div aria-live="polite" className="min-h-[24px]">
                  {statusMessage && (
                    <div className="text-sm text-gray-700 bg-gray-50 p-3 rounded-md border">
                      {statusMessage}
                    </div>
                  )}
                </div>

                <p className="text-xs text-gray-500">
                  By pre-registering you agree to receive occasional emails
                  about the program and related resources. We'll never spam you.
                </p>
              </div>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
};

export default FaithThatWorks;
